import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Html5Qrcode } from 'html5-qrcode';

const READER_ID = 'yarn-box-scanner-reader';

// Camera QR scanner modal. Stays open for continuous scanning — each decoded QR is passed to
// onScan (deduped so one label isn't fired repeatedly). Used at Yarn Outward to scan box labels.
const BoxScanner: React.FC<{ open: boolean; onClose: () => void; onScan: (code: string) => void; footer?: React.ReactNode }> = ({ open, onClose, onScan, footer }) => {
  const scannerRef = useRef<Html5Qrcode | null>(null);
  const onScanRef = useRef(onScan);
  onScanRef.current = onScan;
  const lastScan = useRef<{ code: string; at: number }>({ code: '', at: 0 });
  const [error, setError] = useState('');

  useEffect(() => {
    if (!open) return;
    let stopped = false;
    setError('');
    // Wait a tick so the reader div is mounted before starting the camera.
    const startTimer = setTimeout(() => {
      if (stopped) return;
      const scanner = new Html5Qrcode(READER_ID, { verbose: false } as any);
      scannerRef.current = scanner;
      scanner.start(
        { facingMode: 'environment' },
        { fps: 10, qrbox: { width: 240, height: 240 } },
        (decodedText: string) => {
          const now = Date.now();
          const code = decodedText.trim();
          // Ignore repeated reads of the same label within 2.5s
          if (code === lastScan.current.code && now - lastScan.current.at < 2500) return;
          lastScan.current = { code, at: now };
          onScanRef.current(code);
        },
        () => { /* per-frame decode miss — ignore */ }
      ).catch((e: any) => {
        if (!stopped) setError(e?.message || 'Could not start the camera. Check camera permission and that no other app is using it.');
      });
    }, 60);

    return () => {
      stopped = true;
      clearTimeout(startTimer);
      const s = scannerRef.current;
      scannerRef.current = null;
      if (s) {
        s.stop().then(() => s.clear()).catch(() => { try { s.clear(); } catch (_) { /* noop */ } });
      }
    };
  }, [open]);

  if (!open) return null;

  return createPortal(
    <div className="fixed inset-0 z-[10000] flex items-center justify-center bg-black/60 p-4">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-md overflow-hidden" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-5 py-4 border-b border-[#E8EBF1]">
          <div>
            <h3 className="text-lg font-bold text-[#1B202A]">Scan Box</h3>
            <p className="text-xs text-[rgba(14,19,27,0.5)]">Point the camera at a box's QR label — keep scanning to add more.</p>
          </div>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-[#F8F9FC] text-gray-500">
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
          </button>
        </div>
        <div className="p-4">
          <div id={READER_ID} className="w-full rounded-xl overflow-hidden bg-black min-h-[240px]" />
          {error && <p className="mt-3 text-sm text-red-600">{error}</p>}
          {footer}
          <button onClick={onClose} className="mt-4 w-full px-4 py-2 text-sm font-semibold text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors">
            Done
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default BoxScanner;
